/**
 * Arama penceresinin içeriği — panel playground'undaki
 * `app/(main)/tools/ai-search/playground/components/suggestion-view.ts`
 * dosyasının widget kopyası (widget kendi kendine yeter, panelden import etmez).
 * Mantık değişirse İKİ dosya da güncellenir.
 *
 * Tasarım kararı (Ar-Ge tuvali "V1 · Görselli öneriler", 2026-10-02):
 * - Marka/kategori önerisi aynı yanıttaki gruplarda da varsa öneriden çıkar.
 * - Ürün adı önerisine aynı yanıttaki ürünün görseli (yoksa arama ikonu).
 * - Yazmadan açılışta: kategoriler, markalar, öne çıkan ürünler.
 */

import type {
  AiSearchQueryResponse,
  IAiSearchAutocompleteResponse,
  IAiSearchQueryResult,
} from "./search-core";

/** Türkçe duyarlı karşılaştırma anahtarı: `İ`→`i`, `I`→`ı`, boşluklar tekleşir. */
export const foldText = (value: string) =>
  value.toLocaleLowerCase("tr").replace(/\s+/g, " ").trim();

export interface ISuggestionView {
  text: string;
  type: string;
  typeLabel: string;
  image: string | null;
}

export interface IGroupView {
  /** Filtre anahtarı: `category`, `brand`, `model`. */
  type: string;
  label: string;
  items: { name: string; count: number }[];
}

/** Pencerede çizilen içerik — öneri modunda da açılışta da aynı üç parça. */
export interface IPanelView {
  suggestions: ISuggestionView[];
  groups: IGroupView[];
  products: IAiSearchQueryResult[];
  /** "Tüm sonuçlar" sayısı. */
  found: number;
  /** Açılış görünümü mü (yazılmadan). */
  browsing: boolean;
}

export const suggestView = (data: IAiSearchAutocompleteResponse): IPanelView => {
  const grouped = new Set(
    data.groups.flatMap((group) =>
      group.items.map((item) => `${group.type}|${foldText(item.name)}`),
    ),
  );

  const suggestions = data.suggestions
    .filter(
      (item) =>
        item.type === "title" ||
        !grouped.has(`${item.type}|${foldText(item.text)}`),
    )
    .map((item) => {
      if (item.type !== "title") return { ...item, image: null };
      const key = foldText(item.text);
      const product = data.products.find(
        (candidate) =>
          !!candidate.image && foldText(candidate.title).startsWith(key),
      );
      return { ...item, image: product?.image ?? null };
    });

  return {
    suggestions,
    groups: data.groups,
    products: data.products,
    found: data.found,
    browsing: false,
  };
};

const BROWSE_GROUP_KEYS = ["category", "brand", "model"];
const BROWSE_GROUP_LIMIT = 8;
const BROWSE_PRODUCT_LIMIT = 6;

/** Boş sorgulu arama yanıtından açılış görünümü. */
export const browseView = (data: AiSearchQueryResponse): IPanelView => ({
  suggestions: [],
  groups: data.facets
    .filter(
      (facet) =>
        facet.type === "value" && BROWSE_GROUP_KEYS.includes(facet.key),
    )
    .sort(
      (a, b) =>
        BROWSE_GROUP_KEYS.indexOf(a.key) - BROWSE_GROUP_KEYS.indexOf(b.key),
    )
    .map((facet) => ({
      type: facet.key,
      label: facet.label,
      items: (facet.values ?? [])
        .filter((value) => (value.count ?? 0) > 0)
        .slice(0, BROWSE_GROUP_LIMIT)
        .map((value) => ({ name: String(value.value), count: value.count ?? 0 })),
    }))
    .filter((group) => group.items.length > 0),
  products: data.results.slice(0, BROWSE_PRODUCT_LIMIT),
  found: data.found,
  browsing: true,
});

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/**
 * Metni kaçışlı HTML olarak döndürür; sorgudaki kelimelerin (en az 2 harf)
 * geçtiği yerler `<strong class="shs-mark">` içinde. `tr` küçük harf dönüşümü
 * karakter sayısını korumazsa vurgu yapılmaz.
 */
export const highlightHtml = (text: string, query: string) => {
  const lower = text.toLocaleLowerCase("tr");
  const tokens = foldText(query)
    .split(" ")
    .filter((token) => token.length >= 2);
  if (!tokens.length || lower.length !== text.length) return escapeHtml(text);

  const marked = new Array<boolean>(text.length).fill(false);
  tokens.forEach((token) => {
    let index = lower.indexOf(token);
    while (index !== -1) {
      for (let i = index; i < index + token.length; i += 1) marked[i] = true;
      index = lower.indexOf(token, index + token.length);
    }
  });

  let html = "";
  let run = "";
  let runMarked = marked[0];
  const flush = () => {
    if (!run) return;
    html += runMarked
      ? `<strong class="shs-mark">${escapeHtml(run)}</strong>`
      : escapeHtml(run);
    run = "";
  };
  for (let i = 0; i < text.length; i += 1) {
    if (marked[i] !== runMarked) {
      flush();
      runMarked = marked[i];
    }
    run += text[i];
  }
  flush();
  return html;
};
